import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/useAuth'
import { useWorkspace } from '../context/useWorkspace'
import { listMyFamilyLearners, type FamilyLearnerRow } from '../services/eduFamilyService'
import { isModuleEnabled } from '../utils/workspaceModules'
import type { WorkspaceRole } from '../types/workspace'

export type FamilyAccessStatus = 'loading' | 'ready' | 'error'

export interface FamilyAccessResult {
  status: FamilyAccessStatus
  learners: FamilyLearnerRow[]
  hasFamily: boolean
  isFamilyOnly: boolean
  canSwitchToFamily: (workspaceId: string) => boolean
  refresh: () => void
}

const learnersCache = new Map<string, FamilyLearnerRow[]>()

export const clearFamilyAccessCacheForTesting = () => {
  learnersCache.clear()
}

const extractActiveRolesAndLearnerWorkspaces = (
  workspaces: Array<{ id: string; isPreview?: boolean }> | undefined,
  getWorkspaceRole?: (id: string) => WorkspaceRole | null | undefined,
  rolesByWorkspaceId?: Map<string, WorkspaceRole>,
): { activeRoles: WorkspaceRole[]; learnerWorkspaceIds: string[] } => {
  const activeRoles: WorkspaceRole[] = []
  const learnerWorkspaceIds: string[] = []
  const nonPreviewWorkspaces = (workspaces || []).filter((w) => !w.isPreview)

  if (nonPreviewWorkspaces.length > 0) {
    for (const w of nonPreviewWorkspaces) {
      const role = getWorkspaceRole ? getWorkspaceRole(w.id) : rolesByWorkspaceId?.get(w.id)
      if (role) {
        activeRoles.push(role)
        if (role === 'LEARNER') {
          learnerWorkspaceIds.push(w.id)
        }
      }
    }
  } else if (rolesByWorkspaceId && rolesByWorkspaceId.size > 0) {
    for (const [wsId, role] of rolesByWorkspaceId) {
      if (role !== 'READONLY') {
        activeRoles.push(role)
        if (role === 'LEARNER') {
          learnerWorkspaceIds.push(wsId)
        }
      }
    }
  }

  return { activeRoles, learnerWorkspaceIds: Array.from(new Set(learnerWorkspaceIds)).sort() }
}

export const useIsFamilyOnly = (): boolean => {
  const { workspaces, getWorkspaceRole, rolesByWorkspaceId } = useWorkspace()
  const { activeRoles } = extractActiveRolesAndLearnerWorkspaces(workspaces, getWorkspaceRole, rolesByWorkspaceId)
  return activeRoles.length > 0 && activeRoles.every((role) => role === 'LEARNER')
}

export const useFamilyAccess = (): FamilyAccessResult => {
  const { currentUser } = useAuth()
  const uid = currentUser?.uid ?? null
  const { workspaces, activeWorkspaceId, getWorkspaceRole, rolesByWorkspaceId } = useWorkspace()

  const isFamilyOnly = useIsFamilyOnly()
  const { learnerWorkspaceIds } = extractActiveRolesAndLearnerWorkspaces(workspaces, getWorkspaceRole, rolesByWorkspaceId)
  const hasLearnerRole = learnerWorkspaceIds.length > 0

  const activeWorkspace = workspaces?.find((w) => w.id === activeWorkspaceId)
  const hasEduModuleActive = Boolean(
    activeWorkspace &&
      (isModuleEnabled(activeWorkspace, 'teaching') || isModuleEnabled(activeWorkspace, 'training')),
  )

  const isFamilyRoute = typeof window !== 'undefined' &&
    (window.location.pathname === '/family' || window.location.pathname.startsWith('/family/'))

  const shouldFetch = Boolean(uid && (isFamilyRoute || hasLearnerRole || hasEduModuleActive))
  const cacheKey = shouldFetch && uid ? `${uid}:${learnerWorkspaceIds.join(',')}` : ''

  const [activeKey, setActiveKey] = useState<string>(cacheKey)
  const [learners, setLearners] = useState<FamilyLearnerRow[]>(() =>
    cacheKey && learnersCache.has(cacheKey) ? learnersCache.get(cacheKey)! : [],
  )
  const [status, setStatus] = useState<FamilyAccessStatus>(() => {
    if (!shouldFetch) return 'ready'
    return cacheKey && learnersCache.has(cacheKey) ? 'ready' : 'loading'
  })
  const [refreshSeq, setRefreshSeq] = useState(0)

  if (activeKey !== cacheKey) {
    setActiveKey(cacheKey)
    if (!shouldFetch) {
      setLearners([])
      setStatus('ready')
    } else if (cacheKey && learnersCache.has(cacheKey)) {
      setLearners(learnersCache.get(cacheKey)!)
      setStatus('ready')
    } else {
      setLearners([])
      setStatus('loading')
    }
  }

  const refresh = useCallback(() => {
    if (cacheKey) {
      learnersCache.delete(cacheKey)
    }
    setStatus('loading')
    setRefreshSeq((s) => s + 1)
  }, [cacheKey])

  useEffect(() => {
    if (!shouldFetch || !cacheKey) {
      let active = true
      const reset = async () => {
        if (!active) return
        setLearners([])
        setStatus('ready')
      }
      void reset()
      return () => {
        active = false
      }
    }

    if (learnersCache.has(cacheKey)) {
      return
    }

    let active = true

    const load = async () => {
      try {
        const result = await listMyFamilyLearners()
        if (!active) return
        learnersCache.set(cacheKey, result.learners)
        setLearners(result.learners)
        setStatus('ready')
      } catch {
        if (!active) return
        setStatus('error')
      }
    }

    void load()

    return () => {
      active = false
    }
  }, [shouldFetch, cacheKey, refreshSeq])

  const hasFamily = learners.length > 0

  const canSwitchToFamily = useCallback(
    (workspaceId: string): boolean => {
      if (!workspaceId) return false
      return learners.some((row) => row.workspaceId === workspaceId && row.isStaffHere)
    },
    [learners],
  )

  return {
    status,
    learners,
    hasFamily,
    isFamilyOnly,
    canSwitchToFamily,
    refresh,
  }
}
